import { useEffect, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { VENDOR_PERSON_LIMITS } from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import type { VendorPersonFormValues } from './model/useOffVendorPersons';

interface VendorPersonFormProps {
    form: VendorPersonFormValues;
    saving: boolean;
    onChange: (patch: Partial<VendorPersonFormValues>) => void;
    onCancel: () => void;
    onSubmit: () => void;
}

const VendorPersonForm = ({ form, saving, onChange, onCancel, onSubmit }: VendorPersonFormProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const nameRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => nameRef.current?.focus(), 100);
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <div className="d-flex flex-column h-100 vendor-person-form">
            <div className="off-coming-content px-3 pb-3">
                <Form.Group className="mb-2">
                    <Form.Label className="text-muted">{t('persons.fields.fullName')}</Form.Label>
                    <Form.Control
                        ref={nameRef}
                        value={form.fullName}
                        disabled={saving}
                        maxLength={VENDOR_PERSON_LIMITS.fullName}
                        placeholder={t('persons.placeholders.fullName')}
                        onChange={(e) => onChange({ fullName: e.target.value })}
                    />
                </Form.Group>
                <Form.Group className="mb-2">
                    <Form.Label className="text-muted">{t('persons.fields.position')}</Form.Label>
                    <Form.Control
                        value={form.position}
                        disabled={saving}
                        maxLength={VENDOR_PERSON_LIMITS.position}
                        placeholder={t('persons.placeholders.position')}
                        onChange={(e) => onChange({ position: e.target.value })}
                    />
                </Form.Group>
                <Form.Group className="mb-0">
                    <Form.Label className="text-muted">{t('persons.fields.description')}</Form.Label>
                    <Form.Control
                        as="textarea"
                        className="vendor-person-description"
                        rows={5}
                        value={form.description}
                        disabled={saving}
                        maxLength={VENDOR_PERSON_LIMITS.description}
                        placeholder={t('persons.placeholders.description')}
                        onChange={(e) => onChange({ description: e.target.value })}
                    />
                </Form.Group>
            </div>
            <div className="p-3 offcanvas-footer off-coming-footer">
                <div className="d-flex justify-content-end gap-2">
                    <Button type="button" variant="light-secondary" disabled={saving} onClick={onCancel}>
                        {t('persons.cancelEdit')}
                    </Button>
                    <Button
                        type="button"
                        variant="info"
                        className="min100px"
                        disabled={saving}
                        onClick={onSubmit}
                    >
                        {spinnerTitle(
                            <>
                                <SvgSave className="me-2 mtop-2" />
                                {t('persons.save')}
                            </>,
                            saving,
                            t('common:buttons.saving'),
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default VendorPersonForm;
